import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { publicIdSchema } from "./public-id";

// The clinician corrects the visit's transcript (the first recording; see
// docs/rest-reference-api.md). The previous transcript and note are kept as a
// visit version before the write, so the original stays recoverable.

// Same limit as a typed transcript (visitTranscriptCreateSchema) and the
// note assistant's transcript bound.
export const TRANSCRIPT_EDIT_MAX_BYTES = 60000;
const utf8Bytes = (value: string): number =>
  new TextEncoder().encode(value).length;
// SHA-256 (hex) of the stored transcript text, "" when there is none.
export const transcriptVersionSchema = z.string().regex(/^[0-9a-f]{64}$/);

export const visitTranscriptUpdateSchema = z.strictObject({
  transcription: z
    .string()
    .refine((value) => value.trim().length > 0, "Transcript is required")
    .refine(
      (value) => utf8Bytes(value) <= TRANSCRIPT_EDIT_MAX_BYTES,
      `Transcript exceeds ${TRANSCRIPT_EDIT_MAX_BYTES} UTF-8 bytes`,
    ),
  // The version the edit started from; a transcript that changed since
  // (another tab, retranscription, speaker roles) is never overwritten.
  baseVersion: transcriptVersionSchema,
});
export type VisitTranscriptUpdate = z.output<
  typeof visitTranscriptUpdateSchema
>;
export const visitTranscriptSchema = z.object({
  transcription: z.string(),
  version: transcriptVersionSchema,
  // False when the text was already the same; nothing was written.
  changed: z.boolean(),
});
export type VisitTranscript = z.output<typeof visitTranscriptSchema>;
export const transcriptConflictSchema = z.object({
  reason: z.enum(["stale_transcript", "busy"]),
});

export const visitTranscriptContract = {
  updateVisitTranscript: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      NOT_FOUND: { message: "Visit not found" },
      CONFLICT: { message: "Transcript changed" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      SERVICE_UNAVAILABLE: { message: "Transcript edit unavailable" },
    })
    .input(
      z.object({
        params: z.strictObject({ public_id: publicIdSchema }),
        query: z.strictObject({}).default({}),
        body: visitTranscriptUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PUT",
        path: "/api/v2/visits/{public_id}/transcript",
        inputStructure: "detailed",
        successStatus: 200,
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(visitTranscriptSchema),
};

// ---------- corrections between two transcript texts ----------

export type TranscriptCorrection = { before: string; after: string };

// Words with their trailing whitespace, so joining tokens restores the text.
const tokens = (text: string): string[] => text.match(/\S+\s*|\s+/g) ?? [];
const words = (part: string[]): string => part.join("").trim();

type Op = { op: "equal" | "delete" | "insert"; items: string[] };
// Longest common subsequence diff after trimming the common ends. Inputs
// whose middle is too large for the table are one replacement.
const MAX_CELLS = 4_000_000;
const diff = (
  a: string[],
  b: string[],
  same = (x: string, y: string) => x === y,
): Op[] => {
  let start = 0;
  while (start < a.length && start < b.length && same(a[start], b[start]))
    start++;
  let endA = a.length,
    endB = b.length;
  while (endA > start && endB > start && same(a[endA - 1], b[endB - 1])) {
    endA--;
    endB--;
  }
  const head: Op[] = start ? [{ op: "equal", items: a.slice(0, start) }] : [];
  const tail: Op[] =
    endA < a.length ? [{ op: "equal", items: a.slice(endA) }] : [];
  const midA = a.slice(start, endA),
    midB = b.slice(start, endB);
  const n = midA.length,
    m = midB.length;
  const middle: Op[] = [];
  if (n && m && (n + 1) * (m + 1) <= MAX_CELLS) {
    const width = m + 1;
    const table = new Uint32Array((n + 1) * width);
    for (let i = n - 1; i >= 0; i--)
      for (let j = m - 1; j >= 0; j--)
        table[i * width + j] = same(midA[i], midB[j])
          ? table[(i + 1) * width + j + 1] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + j + 1]);
    const push = (op: Op["op"], item: string) => {
      const last = middle[middle.length - 1];
      if (last?.op === op) last.items.push(item);
      else middle.push({ op, items: [item] });
    };
    let i = 0,
      j = 0;
    while (i < n && j < m) {
      if (same(midA[i], midB[j])) {
        push("equal", midA[i]);
        i++;
        j++;
      } else if (table[(i + 1) * width + j] >= table[i * width + j + 1])
        push("delete", midA[i++]);
      else push("insert", midB[j++]);
    }
    while (i < n) push("delete", midA[i++]);
    while (j < m) push("insert", midB[j++]);
  } else {
    if (n) middle.push({ op: "delete", items: midA });
    if (m) middle.push({ op: "insert", items: midB });
  }
  return [...head, ...middle, ...tail];
};

// Unchanged words kept on each side of a change, so "fifteen" → "fifty"
// reads as "metoprolol fifteen milligrams" → "metoprolol fifty milligrams".
const CONTEXT_WORDS = 2;
const lineCorrections = (
  before: string,
  after: string,
): TranscriptCorrection[] => {
  const ops = diff(
    tokens(before),
    tokens(after),
    (x, y) => x.trim() === y.trim(),
  );
  const out: TranscriptCorrection[] = [];
  let pending: { before: string[]; after: string[]; lead: string[] } | null =
    null;
  let trailing: string[] = [];
  const flush = (follow: string[]) => {
    if (!pending) return;
    const tail = follow.slice(0, CONTEXT_WORDS);
    const b = words([...pending.lead, ...pending.before, ...tail]);
    const a = words([...pending.lead, ...pending.after, ...tail]);
    if (b !== a) out.push({ before: b, after: a });
    pending = null;
  };
  ops.forEach((op, index) => {
    const items = op.items.filter((item) => item.trim());
    if (op.op === "equal") {
      // A short run of unchanged words between two changes joins them.
      if (
        pending &&
        index < ops.length - 1 &&
        items.length <= CONTEXT_WORDS * 2
      ) {
        pending.before.push(...items);
        pending.after.push(...items);
        trailing = items;
        return;
      }
      flush(items);
      trailing = items;
      return;
    }
    pending ??= { before: [], after: [], lead: trailing.slice(-CONTEXT_WORDS) };
    (op.op === "delete" ? pending.before : pending.after).push(...items);
  });
  flush([]);
  return out;
};

// The changes between two versions of a transcript, as short before/after
// snippets with a little unchanged context. Lines (speaker turns) are matched
// first, then words inside changed lines. Whitespace-only edits are ignored.
export const transcriptCorrections = (
  before: string,
  after: string,
): TranscriptCorrection[] => {
  const lines = (text: string) =>
    text
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter(Boolean);
  const ops = diff(lines(before), lines(after));
  const out: TranscriptCorrection[] = [];
  for (let index = 0; index < ops.length; index++) {
    const op = ops[index];
    if (op.op === "equal") continue;
    const next = ops[index + 1];
    const removed = op.op === "delete" ? op.items : [];
    const paired = op.op === "delete" && next?.op === "insert";
    if (paired) index++;
    const added =
      op.op === "insert" ? op.items : paired && next ? next.items : [];
    // Pair changed lines one to one; extra lines are whole insertions or
    // removals.
    const pairs = Math.max(removed.length, added.length);
    for (let line = 0; line < pairs; line++)
      out.push(...lineCorrections(removed[line] ?? "", added[line] ?? ""));
  }
  return out;
};
